import React, { useMemo, useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  TextField,
  InputAdornment,
  Chip,
  Grid,
  Paper,
  Divider,
  Stack,
  Button
} from '@mui/material';
import ExpandMore from '@mui/icons-material/ExpandMore';
import Search from '@mui/icons-material/Search';
import HelpOutline from '@mui/icons-material/HelpOutline';
import CheckCircleOutline from '@mui/icons-material/CheckCircleOutline';
import { Link } from 'react-router-dom';

const allFaqs = [
  {
    question: 'Wann sollte ich mit der Planung meines Umzugs beginnen?',
    answer:
      'Idealerweise sollten Sie mindestens 6 bis 8 Wochen vor dem Umzug mit der Planung beginnen. So haben Sie ausreichend Zeit, ein Umzugsunternehmen zu buchen, Umzugskartons zu besorgen und alles zu organisieren.',
    tag: 'Planung'
  },
  {
    question: 'Wie finde ich ein zuverlässiges Umzugsunternehmen?',
    answer:
      'Recherchieren Sie online nach Bewertungen und Erfahrungen anderer Kunden. Holen Sie mehrere Angebote ein, vergleichen Sie die Preise und prüfen Sie, ob das Unternehmen zertifiziert oder versichert ist.',
    tag: 'Unternehmen'
  },
  {
    question: 'Welche Kosten kommen bei einem Umzug auf mich zu?',
    answer:
      'Die Kosten hängen von vielen Faktoren ab, wie der Entfernung, der Menge der zu transportierenden Gegenstände, der benötigten Anzahl an Helfern und den Zusatzleistungen (z. B. Verpackung). Die Preise können zwischen ein paar hundert und mehreren tausend Euro variieren.',
    tag: 'Kosten'
  },
  {
    question: 'Wie viele Umzugskartons benötige ich?',
    answer:
      'Eine grobe Faustregel ist, dass pro Quadratmeter Wohnfläche etwa 1 bis 1,5 Umzugskartons benötigt werden. Für eine 50 m² große Wohnung wären das also etwa 50 bis 75 Kartons.',
    tag: 'Verpackung'
  },
  {
    question: 'Welche Versicherungen sind für einen Umzug notwendig?',
    answer:
      'Es ist ratsam, eine Transportversicherung abzuschließen, die den Schaden an Ihren Möbeln während des Umzugs abdeckt. Viele Umzugsunternehmen bieten auch eigene Versicherungen an. Zudem kann Ihre Hausratversicherung den Umzug teilweise abdecken.',
    tag: 'Versicherung'
  },
  {
    question: 'Kann ich Umzugskosten von der Steuer absetzen?',
    answer:
      'Ja, Umzugskosten können unter bestimmten Bedingungen steuerlich abgesetzt werden, z. B. wenn der Umzug beruflich bedingt ist oder wenn ein medizinischer Grund vorliegt. Auch bei einem privaten Umzug können Sie haushaltsnahe Dienstleistungen absetzen.',
    tag: 'Steuern'
  },
  {
    question: 'Wie packe ich meine Möbel und Gegenstände richtig ein?',
    answer:
      'Beginnen Sie mit selten genutzten Gegenständen. Verpacken Sie schwere Gegenstände in kleinen Kartons und leichtere in größeren Kartons. Wickeln Sie Zerbrechliches in Luftpolsterfolie oder Tücher ein. Beschriften Sie alle Kartons klar nach Raum und Inhalt.',
    tag: 'Verpackung'
  },
  {
    question: 'Was muss ich bei einem Umzug in eine andere Stadt beachten?',
    answer:
      'Informieren Sie sich über die Gegebenheiten vor Ort (Parkmöglichkeiten, Umzugsgenehmigungen). Melden Sie Ihren Wohnsitz rechtzeitig um, melden Sie Kinder in der neuen Schule an und schließen Sie neue Verträge für Strom, Internet und Wasser ab.',
    tag: 'Organisation'
  },
  {
    question: 'Wie organisiere ich den Umzug mit Kindern oder Haustieren?',
    answer:
      'Organisieren Sie für den Umzugstag eine Betreuung, um Stress zu minimieren. Packen Sie eine Tasche mit wichtigen Dingen (Spielzeug, Snacks, Medikamente) für Kinder und bereiten Sie eine Transportbox mit Futter, Wasser und Spielzeug für Haustiere vor.',
    tag: 'Familie'
  },
  {
    question: 'Wie kann ich meinen Umzug so umweltfreundlich wie möglich gestalten?',
    answer:
      'Verwenden Sie wiederverwendbare Verpackungsmaterialien, vermeiden Sie Einwegplastik, spenden oder recyceln Sie nicht benötigte Gegenstände und wählen Sie, wenn möglich, ein Umzugsunternehmen mit umweltfreundlichen Fahrzeugen und Methoden.',
    tag: 'Nachhaltigkeit'
  },
  {
    question: 'Welche Fristen muss ich bei der Ummeldung beachten?',
    answer:
      'In Deutschland müssen Sie sich innerhalb von zwei Wochen nach dem Umzug beim Einwohnermeldeamt ummelden. Versäumnisse können eine Geldstrafe nach sich ziehen.',
    tag: 'Ummeldung'
  },
  {
    question: 'Sollte ich selbst umziehen oder ein Umzugsunternehmen beauftragen?',
    answer:
      'Das hängt von Budget, Umfang des Umzugsguts und Ihrer Zeit sowie körperlichen Möglichkeiten ab. Ein Unternehmen reduziert Aufwand und Risiko, während Selbst-Umzüge günstiger sein können.',
    tag: 'Entscheidung'
  }
];

const popularTags = ['Planung', 'Kosten', 'Verpackung', 'Versicherung', 'Steuern'];

const FAQ = () => {
  const [query, setQuery] = useState('');
  const [selectedTag, setSelectedTag] = useState('');

  const filteredFaqs = useMemo(() => {
    const q = query.trim().toLowerCase();
    return allFaqs.filter((f) => {
      const matchesText =
        !q ||
        f.question.toLowerCase().includes(q) ||
        f.answer.toLowerCase().includes(q) ||
        f.tag.toLowerCase().includes(q);
      const matchesTag = !selectedTag || f.tag === selectedTag;
      return matchesText && matchesTag;
    });
  }, [query, selectedTag]);

  return (
    <Box sx={{ mt: { xs: -7, md: -8 } }}>
      {/* Hero */}
      <Box
        sx={{
          py: { xs: 8, md: 10 },
          background:
            'radial-gradient(1200px 600px at 10% -20%, rgba(85,214,220,0.25), transparent 60%), radial-gradient(800px 500px at 110% -10%, rgba(255,215,0,0.15), transparent 60%)',
          borderBottom: '1px solid rgba(255,255,255,0.08)'
        }}
      >
        <Container maxWidth="lg">
          <Grid container spacing={4} alignItems="center">
            <Grid item xs={12} md={7}>
              <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                <HelpOutline sx={{ color: '#ffd700' }} />
                <Typography variant="overline" sx={{ color: '#ffd700', letterSpacing: 1.2 }}>
                  FAQ - Fragen und Antworten
                </Typography>
              </Stack>
              <Typography
                variant="h3"
                sx={{
                  fontWeight: 'bold',
                  color: 'white',
                  mb: 1,
                  textShadow: '0 1px 0 rgba(0,0,0,0.2)'
                }}
              >
                Alles rund um Ihren Umzug
              </Typography>
              <Typography variant="body1" sx={{ color: 'white', opacity: 0.85 }}>
                Antworten auf die häufigsten Fragen – kompakt, modern und auf den Punkt. Nutzen Sie die Suche oder filtern Sie nach Themen.
              </Typography>
            </Grid>
            <Grid item xs={12} md={5}>
              <Paper
                elevation={0}
                sx={{
                  p: 2,
                  bgcolor: '#1a3a44',
                  borderRadius: 2,
                  border: '1px solid rgba(255,255,255,0.08)'
                }}
              >
                <TextField
                  fullWidth
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Suche nach Thema, Frage oder Stichwort …"
                  variant="outlined"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Search sx={{ color: '#55d6dc' }} />
                      </InputAdornment>
                    )
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      color: 'white',
                      '& fieldset': { borderColor: 'rgba(255,255,255,0.15)' },
                      '&:hover fieldset': { borderColor: '#55d6dc' },
                      '&.Mui-focused fieldset': { borderColor: '#55d6dc' }
                    }
                  }}
                />

                <Stack direction="row" spacing={1} sx={{ mt: 2, flexWrap: 'wrap', gap: 1 }}>
                  {popularTags.map((tag) => {
                    const active = selectedTag === tag;
                    return (
                      <Chip
                        key={tag}
                        label={tag}
                        onClick={() => setSelectedTag(active ? '' : tag)}
                        variant={active ? 'filled' : 'outlined'}
                        color="primary"
                        sx={{
                          bgcolor: active ? '#55d6dc' : 'transparent',
                          color: active ? '#275664' : 'white',
                          borderColor: '#55d6dc',
                          '&:hover': { bgcolor: active ? '#4bc2c8' : 'rgba(85,214,220,0.15)' }
                        }}
                      />
                    );
                  })}
                  {selectedTag && (
                    <Chip
                      label="Filter löschen"
                      onClick={() => setSelectedTag('')}
                      sx={{
                        bgcolor: 'rgba(255,255,255,0.08)',
                        color: 'white',
                        '&:hover': { bgcolor: 'rgba(255,255,255,0.15)' }
                      }}
                    />
                  )}
                </Stack>

                <Typography variant="caption" sx={{ display: 'block', mt: 1.5, color: 'white', opacity: 0.6 }}>
                  {filteredFaqs.length} Ergebnis(se) angezeigt
                </Typography>
              </Paper>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* FAQ List */}
      <Box sx={{ py: 8, bgcolor: '#275664' }}>
        <Container maxWidth="md">
          {filteredFaqs.map((item, idx) => (
            <Accordion
              key={item.question + idx}
              disableGutters
              sx={{
                mb: 2,
                bgcolor: '#1a3a44',
                color: 'white',
                borderRadius: 2,
                overflow: 'hidden',
                border: '1px solid rgba(255,255,255,0.08)',
                '&::before': { display: 'none' },
                '& .MuiAccordionSummary-root': {
                  '&:hover': { bgcolor: 'rgba(85,214,220,0.08)' }
                }
              }}
            >
              <AccordionSummary
                expandIcon={<ExpandMore sx={{ color: '#ffd700' }} />}
                aria-controls={`panel-${idx}-content`}
                id={`panel-${idx}-header`}
              >
                <Stack direction="row" alignItems="center" spacing={1}>
                  <CheckCircleOutline sx={{ color: '#55d6dc' }} />
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>{item.question}</Typography>
                  {item.tag && (
                    <Chip
                      label={item.tag}
                      size="small"
                      sx={{
                        ml: 1,
                        bgcolor: 'rgba(255,215,0,0.12)',
                        color: '#ffd700',
                        border: '1px solid rgba(255,215,0,0.35)'
                      }}
                    />
                  )}
                </Stack>
              </AccordionSummary>
              <AccordionDetails>
                <Typography sx={{ opacity: 0.9, lineHeight: 1.7 }}>{item.answer}</Typography>
              </AccordionDetails>
            </Accordion>
          ))}

          {/* No results state */}
          {filteredFaqs.length === 0 && (
            <Paper
              elevation={0}
              sx={{
                mt: 4,
                p: 4,
                textAlign: 'center',
                bgcolor: '#1a3a44',
                borderRadius: 2,
                border: '1px solid rgba(255,255,255,0.08)'
              }}
            >
              <Typography variant="h6" sx={{ color: 'white', mb: 1 }}>
                Keine Ergebnisse
              </Typography>
              <Typography variant="body2" sx={{ color: 'white', opacity: 0.7 }}>
                Passen Sie Ihre Suche an oder entfernen Sie Filter, um weitere Ergebnisse zu sehen.
              </Typography>
            </Paper>
          )}
        </Container>
      </Box>

      {/* Guide / Info Section */}
      <Box sx={{ pb: 10, bgcolor: '#275664' }}>
        <Container maxWidth="md">
          <Paper
            elevation={0}
            sx={{
              p: { xs: 3, md: 4 },
              bgcolor: '#0f2832',
              borderRadius: 3,
              border: '1px solid rgba(255,255,255,0.08)',
              backgroundImage:
                'linear-gradient(135deg, rgba(85,214,220,0.06) 0%, rgba(255,215,0,0.06) 100%)'
            }}
          >
            <Typography variant="h5" sx={{ color: 'white', fontWeight: 'bold', mb: 1 }}>
              Richtig Umzugskartons einpacken – Umzugskosten reduzieren
            </Typography>
            <Divider sx={{ borderColor: 'rgba(255,255,255,0.12)', my: 2 }} />
            <Typography variant="body1" sx={{ color: 'white', opacity: 0.9, lineHeight: 1.8, mb: 2 }}>
              Ein Einpackservice bietet eine sichere und professionelle Verpackung von Gegenständen, um sie während des
              Transports oder der Lagerung zu schützen. Diese Dienste sind besonders nützlich für den Versand empfindlicher
              oder wertvoller Gegenstände wie Glaswaren, Elektronik oder Kunstwerke.
            </Typography>
            <Typography variant="body1" sx={{ color: 'white', opacity: 0.9, lineHeight: 1.8, mb: 2 }}>
              Ein professioneller Einpackservice verwendet hochwertige Materialien wie Luftpolsterfolie, Schaumstoff,
              Kartons mit doppelter Wandstärke und spezielle Polsterungen, um die Artikel vor Stößen, Feuchtigkeit und
              anderen potenziellen Schäden zu schützen. Außerdem haben sie Erfahrung in der richtigen Verpackungstechnik, um
              sicherzustellen, dass die Gegenstände während des Transports sicher und stabil bleiben.
            </Typography>
            <Typography variant="body1" sx={{ color: 'white', opacity: 0.9, lineHeight: 1.8, mb: 3 }}>
              Die Inanspruchnahme eines Einpackservices kann helfen, Zeit zu sparen und das Risiko von Schäden zu
              minimieren, insbesondere bei zerbrechlichen oder empfindlichen Gegenständen.
            </Typography>

            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <Button
                component={Link}
                to="/contact"
                variant="contained"
                sx={{
                  bgcolor: '#55d6dc',
                  color: '#275664',
                  fontWeight: 'bold',
                  '&:hover': { bgcolor: '#4bc2c8' }
                }}
              >
                Jetzt Einpackservice anfragen
              </Button>
              <Button
                component={Link}
                to="/services"
                variant="outlined"
                sx={{
                  borderColor: '#ffd700',
                  color: '#ffd700',
                  '&:hover': { borderColor: '#ffd700', bgcolor: 'rgba(255,215,0,0.08)' }
                }}
              >
                Weitere Leistungen ansehen
              </Button>
            </Stack>
          </Paper>
        </Container>
      </Box>
    </Box>
  );
};

export default FAQ;